import { CostingTemplatesSidebar } from '@/components/CostingTemplates/CostingTemplatesSidebar';
import { LayoutCard } from '@/components/LayoutCard';
import { PageLayoutCollapsibleSidebar } from '@/components/PageLayoutCollapsibleSidebar';
import { useHttpQuery } from '@/resources/http/useHttpQuery';
import { useHttpMutation } from '@/resources/mutation/useHttpMutation';
import { UrlParams, route } from '@/utils/routes';
import { Trans, t } from '@lingui/macro';
import { useHasPermission } from '@luminovo/auth';
import { formatLongDateTime, formatRelativeTime, getIntercomArticleURL } from '@luminovo/commons';
import {
    DestructiveSecondaryIconButton,
    Flexbox,
    Link,
    NonIdealState,
    PrimaryButton,
    SecondaryIconButton,
    TanStackTable,
    Text,
    Tooltip,
    colorSystem,
    createColumnHelper,
    useConfirmationDialog,
    useNavigate,
    useTanStackTable,
} from '@luminovo/design-system';
import { CalculationTemplateResponseDTO } from '@luminovo/http-client';
import { CalculateOutlined, Delete, EditRounded } from '@mui/icons-material';
import { Box } from '@mui/material';
import { useIsAdminUser } from '../../../../components/contexts/CurrentUserDetailsContext';

type RowItemType = CalculationTemplateResponseDTO;

const columnHelper = createColumnHelper<RowItemType>();
const columns = [
    columnHelper.text((row) => row.name, {
        id: 'name',
        label: () => t`Name of the template`,
        size: 160,
    }),
    columnHelper.text((row) => row.notes, {
        id: 'notes',
        label: () => t`Notes`,
        size: 160,
    }),
    columnHelper.date((row) => row.updated_at, {
        id: 'updatedAt',
        size: 100,
        label: () => t`Updated`,
        cell: ({ row }) => (
            <Tooltip title={formatLongDateTime(row.original.updated_at)}>
                <Text variant="inherit" color={colorSystem.neutral[8]}>
                    {formatRelativeTime(row.original.updated_at)}
                </Text>
            </Tooltip>
        ),
    }),
    columnHelper.action({
        id: 'actionMenu',
        size: 80,
        cell: ({ row }) => <RowActionButton calculationTemplate={row.original} />,
    }),
];

function RowActionButton({
    calculationTemplate,
}: {
    calculationTemplate: CalculationTemplateResponseDTO;
}): JSX.Element {
    const navigate = useNavigate();

    const { mutateAsync, isPending } = useHttpMutation('DELETE /calculations/templates/:templateId', {
        enableInvalidateAllQueries: false,
        snackbarMessage: t`Calculation template deleted`,
    });

    const { modal, setOpen } = useConfirmationDialog({
        title: t`Delete calculation template`,
        description: t`Are you sure you want to delete this calculation template? This cannot be undone.`,
        onConfirmation: () => mutateAsync({ pathParams: { templateId: calculationTemplate.id } }),
    });

    const disabled = !useHasPermission(['edit:organization_settings']);

    return (
        <>
            {modal}
            <Flexbox gap={'4px'}>
                <Tooltip title={t`Edit calculation template`}>
                    <SecondaryIconButton
                        size="small"
                        disabled={disabled}
                        onClick={() =>
                            navigate(
                                route('/calculation/templates/:templateId/edit', {
                                    templateId: calculationTemplate.id,
                                }),
                            )
                        }
                    >
                        <EditRounded fontSize="inherit" />
                    </SecondaryIconButton>
                </Tooltip>
                <Tooltip title={t`Delete calculation template`}>
                    <DestructiveSecondaryIconButton
                        size="small"
                        disabled={disabled}
                        onClick={() => setOpen(true)}
                        loading={isPending}
                    >
                        <Delete fontSize="inherit" />
                    </DestructiveSecondaryIconButton>
                </Tooltip>
            </Flexbox>
        </>
    );
}

function EmptyPlaceholder(): JSX.Element {
    return (
        <NonIdealState
            Icon={CalculateOutlined}
            title={t`No calculation templates found`}
            description={t`You can create a new calculation template by clicking the button above.`}
        />
    );
}

function ActionButton() {
    const isAdmin = useIsAdminUser();

    if (!isAdmin) {
        return null;
    }

    return (
        <PrimaryButton size={'medium'} href={route('/calculation/templates/add-template')}>
            <Trans>Add template</Trans>
        </PrimaryButton>
    );
}

function InnerContent() {
    const { data } = useHttpQuery('GET /calculations/templates', {}, { select: (res) => res.data });
    const hasPermission = useIsAdminUser();

    const { table } = useTanStackTable({
        data,
        columns,
        enableColumnHiding: true,
        enableColumnOrdering: true,
        getRowRoute: hasPermission
            ? (row) => route('/calculation/templates/:templateId/edit', { templateId: row.original.id })
            : undefined,
    });

    return (
        <Box style={{ height: 'calc(100% - 48px)' }}>
            <TanStackTable table={table} EmptyPlaceholder={EmptyPlaceholder} ActionButton={ActionButton} />
        </Box>
    );
}

export function CalculationTemplatesViewPage(_: UrlParams<'/calculation/templates'>) {
    return (
        <PageLayoutCollapsibleSidebar sidebar={<CostingTemplatesSidebar />} layoutVariant="fullWidth">
            <LayoutCard
                title={t`Calculation`}
                description={
                    <Text variant="body">
                        <Trans>
                            Templates for calculating your final sales price including profit margins, overhead costs,
                            and discounts.
                        </Trans>{' '}
                        <Link
                            variant="body"
                            href={getIntercomArticleURL(166247)}
                            rel="noopener noreferrer"
                            target="_blank"
                        >
                            {t`Learn more`}
                        </Link>
                    </Text>
                }
                style={{ height: '100%' }}
            >
                <InnerContent />
            </LayoutCard>
        </PageLayoutCollapsibleSidebar>
    );
}
